import React, { FC, Suspense, useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  Box,
  ButtonAppearance,
  SideSheet,
  SideSheetBody,
  SideSheetClose,
  SideSheetFooter,
  SideSheetHeader,
  SideSheetSize,
  SideSheetSubHeader,
  Spinner,
  SpinnerSize,
  ToastAction,
  ToastType,
  useModalState,
  useToastsDispatch,
} from '@bechtel/gds';
import {
  Block,
  BlockStatusCode,
  CommodityMilestone,
  ResolveBlockError,
  ResolveBlockInput,
  ResolveBlockPayload,
} from '@bechtel/types';
import { Formik, FormikHelpers as FormikActions } from 'formik';
import { object, string } from 'yup';
import { useBlockPermission, TitleValue, ErrorBoundary, DrawerFooterButtonStyled } from '@bechtel/client-common';
import { RESOLVE_BLOCK } from '@bechtel/client-gql';
import { MultiBlock } from './multi';
import { t, Trans } from '@lingui/macro';
import { FormikBlockResolutionState, ResolveBlockProps } from './types';
import { BlockNotResolved, SingleBlock } from '~/components';

const blockResolutionValidationSchema = object().shape({
  resolution: string()
    .max(2000, t`Please limit resolution notes to 2000 characters`)
    .required(t`Resolution Note is mandatory`),
});

const blockResolutionInitialValues: FormikBlockResolutionState = {
  resolution: '',
};

const getBlocks = (milestone?: CommodityMilestone, block?: Block): any[] => {
  if (block) return [block];
  if (milestone) return milestone.blocks;
  return [];
};

export const ResolveBlock: FC<ResolveBlockProps> = ({
  workPackageId,
  workPackageTag,
  commodityTag,
  commodityTypeName,
  facilities,
  assignedCrewMembers,
  weldCommodityAssignees,
  onClose,
  milestone,
  block,
  refetchQueries = [],
}) => {
  const { canResolveBlock } = useBlockPermission(
    assignedCrewMembers,
    facilities ? facilities : [],
    null,
    weldCommodityAssignees
  );
  const dispatchToast = useToastsDispatch();
  const [resolvedBlocks, setResolvedBlocks] = useState<Block[]>([]);
  const blockList: Block[] = getBlocks(milestone, block);
  const blocks = useState<Block[]>(blockList)[0];
  const [resolveBlockError, setResolveBlockError] = useState<ResolveBlockError>();
  const {
    visible: blockNotResolvedModalVisible,
    open: openBlockNotResolvedModal,
    close: closeBlockNotResolvedModal,
  } = useModalState(false);
  const isSingleBlock = blocks?.length === 1;
  const [selectedBlock, setSelectedBlock] = useState<Block | undefined>(isSingleBlock ? blocks[0] : undefined);
  const headerTitle = isSingleBlock
    ? blocks[0] && blocks[0].currentStatusI18n === BlockStatusCode.BLOCK_RESOLVED
      ? t`Block Details`
      : t`Edit/Resolve Block`
    : t`Edit/Resolve Block(s)`;
  const openResolveBlockToast = (blockCount: number) => {
    if (blockCount > 0) {
      const resolveBlockToast: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.SUCCESS,
          content: `${blockCount} ${blockCount === 1 ? t`Block` : t`Blocks(s)`} ${t`Successfully Resolved`}`,
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(resolveBlockToast);
    }
  };
  const closeSidesheet = () => {
    if (!isSingleBlock) {
      openResolveBlockToast(resolvedBlocks && resolvedBlocks.length);
    } else {
      openResolveBlockToast(1);
    }
    if (onClose) {
      onClose();
    }
  };
  const onResolveBlockComplete = useCallback(
    (data: { resolveBlock: ResolveBlockPayload }) => {
      if (!isSingleBlock) {
        if (data?.resolveBlock?.block) {
          setResolvedBlocks((prev: Block[]) => {
            return [...prev, data?.resolveBlock?.block as Block];
          });
        }
      }
    },
    [isSingleBlock]
  );
  const [resolveBlock, { loading: resolveBlockLoading }] = useMutation<
    { resolveBlock: ResolveBlockPayload },
    { input: ResolveBlockInput }
  >(RESOLVE_BLOCK, { onCompleted: onResolveBlockComplete, refetchQueries: refetchQueries });

  const closeBlockErrorModal = () => {
    setResolveBlockError(undefined);
    closeBlockNotResolvedModal();
  };

  const onSelectBlock = (block: Block) => {
    setSelectedBlock(block);
  };
  return (
    <ErrorBoundary>
      {blockNotResolvedModalVisible && resolveBlockError && (
        <BlockNotResolved
          workPackageId={workPackageId}
          resolveBlockError={resolveBlockError}
          onClose={closeBlockErrorModal}
        />
      )}
      <Formik
        initialValues={blockResolutionInitialValues}
        validationSchema={blockResolutionValidationSchema}
        onSubmit={async (values: FormikBlockResolutionState, actions: FormikActions<FormikBlockResolutionState>) => {
          if (values.resolution) {
            const resolveBlockInput: ResolveBlockInput = {
              id: selectedBlock?.id,
              comment: values.resolution,
            };
            const { data: resolveBlockData } = await resolveBlock({
              variables: { input: resolveBlockInput },
            });
            const resolveBlockError = resolveBlockData?.resolveBlock?.error;
            if (resolveBlockData?.resolveBlock?.block) {
              actions.resetForm({ values: blockResolutionInitialValues });
              actions.setSubmitting(false);
            }
            if (isSingleBlock) {
              if (resolveBlockData?.resolveBlock?.block) {
                closeSidesheet();
              }
            }
            if (resolveBlockError) {
              setResolveBlockError(resolveBlockError);
              openBlockNotResolvedModal();
            }
          }
        }}
      >
        {(formikProps) => {
          return (
            <SideSheet
              onScroll={(e) => {
                e.stopPropagation();
              }}
              visible
              size={isSingleBlock ? SideSheetSize.DEFAULT : SideSheetSize.MEDIUM}
              onDismiss={onClose}
            >
              <SideSheetHeader title={headerTitle}>
                <SideSheetClose onClick={onClose} />
              </SideSheetHeader>
              <SideSheetSubHeader>
                {workPackageTag && (
                  <TitleValue data-testid="wp-tag" title={t`Work Package`} value={workPackageTag}></TitleValue>
                )}
                {commodityTag && commodityTypeName && (
                  <Box mt={{ sm: '300' }}>
                    <TitleValue data-testid="commodity-tag" title={commodityTypeName} value={commodityTag}></TitleValue>
                  </Box>
                )}
                {milestone && (
                  <Box mt={{ sm: '300' }}>
                    <TitleValue data-testid="milestone-name" title={t`Milestone`} value={milestone.name}></TitleValue>
                  </Box>
                )}
              </SideSheetSubHeader>
              <SideSheetBody>
                <ErrorBoundary>
                  <Suspense fallback={<Spinner size={SpinnerSize.LARGE} />}>
                    {!isSingleBlock && (
                      <MultiBlock
                        blockList={blockList}
                        workPackageTag={workPackageTag}
                        assignedCrewMembers={assignedCrewMembers}
                        weldCommodityAssignees={weldCommodityAssignees}
                        facilities={facilities}
                        resolvedBlocks={resolvedBlocks}
                        onSelectBlock={(block) => {
                          onSelectBlock(block);
                        }}
                      />
                    )}
                    {isSingleBlock && selectedBlock && (
                      <SingleBlock
                        blockId={selectedBlock.id}
                        assignedCrewMembers={assignedCrewMembers}
                        weldCommodityAssignees={weldCommodityAssignees}
                        facilities={facilities}
                      />
                    )}
                  </Suspense>
                </ErrorBoundary>
              </SideSheetBody>
              <SideSheetFooter>
                <Box justifyContent={{ sm: 'flex-end' }}>
                  {!isSingleBlock && (
                    <Box mr={{ sm: '400' }}>
                      <DrawerFooterButtonStyled
                        appearance={ButtonAppearance.SECONDARY}
                        onClick={() => {
                          closeSidesheet();
                        }}
                      >
                        <Trans> Done</Trans>
                      </DrawerFooterButtonStyled>
                    </Box>
                  )}
                  {canResolveBlock &&
                    selectedBlock &&
                    selectedBlock.currentStatusI18n === BlockStatusCode.BLOCK_OPENED && (
                      <Box>
                        <DrawerFooterButtonStyled
                          disabled={
                            !formikProps.dirty ||
                            !formikProps.isValid ||
                            !formikProps.values.resolution ||
                            resolveBlockLoading
                          }
                          onClick={() => formikProps.handleSubmit()}
                          appearance={ButtonAppearance.SECONDARY}
                        >
                          {resolveBlockLoading && <Spinner />}
                          {!resolveBlockLoading && <Trans>Resolve Block</Trans>}
                        </DrawerFooterButtonStyled>
                      </Box>
                    )}
                </Box>
              </SideSheetFooter>
            </SideSheet>
          );
        }}
      </Formik>
    </ErrorBoundary>
  );
};
